import { useEffect, useState, CSSProperties } from 'react';
import { getOperatorMessages, OperatorMessage } from '../services/operatorMessages';

interface SendVisitorMessageModalProps {
  /** e.g. "CLEOPATRA-03 · Egypt v1.4 · scene 3". */
  contextLine: string;
  onCancel: () => void;
  /** Must not throw — the parent handles success/failure feedback (toasts). */
  onSubmit: (msg: OperatorMessage) => Promise<void>;
}

/**
 * Preset picker for sending a voice warning/instruction to the visitor in the
 * headset ("Niet rennen"). Presets only — the clip is pre-rendered, so there is
 * no free-text input (and thus no TouchKeyboard). Styling clones MarkIssueModal
 * (fixed backdrop + centered white card).
 */
export default function SendVisitorMessageModal({ contextLine, onCancel, onSubmit }: SendVisitorMessageModalProps) {
  const [messages, setMessages] = useState<OperatorMessage[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getOperatorMessages().then((msgs) => {
      if (!cancelled) setMessages(msgs);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const selected = messages.find((m) => m.key === selectedKey) || null;

  const handleSubmit = async () => {
    if (!selected || sending) return;
    setSending(true);
    try {
      await onSubmit(selected);
    } finally {
      setSending(false);
    }
  };

  const labelStyle: CSSProperties = {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: '#54626f',
    margin: '14px 0 6px',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(16,32,46,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={() => {
        if (!sending) onCancel();
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 14,
          padding: 24,
          width: 'min(460px, calc(100vw - 32px))',
          maxHeight: 'calc(100vh - 24px)',
          overflowY: 'auto',
          boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
          color: '#16202e',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: 0 }}>Bericht naar bezoeker</h3>
        <div style={{ fontSize: 13, color: '#54626f', marginTop: 4 }}>{contextLine}</div>

        <label style={labelStyle}>Bericht *</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {messages.length === 0 && (
            <span style={{ color: '#8a97a6', fontSize: 13 }}>Berichten laden…</span>
          )}
          {messages.map((msg) => {
            const isSel = msg.key === selectedKey;
            return (
              <button
                key={msg.key}
                onClick={() => setSelectedKey(msg.key)}
                disabled={sending}
                style={{
                  padding: '10px 14px',
                  borderRadius: 10,
                  fontSize: 13,
                  cursor: 'pointer',
                  textAlign: 'left',
                  border: isSel ? '1px solid #2B6AC0' : '1px solid #d5dce5',
                  background: isSel ? '#2B6AC0' : '#fff',
                  color: isSel ? '#fff' : '#16202e',
                }}
              >
                <div style={{ fontWeight: 600 }}>{msg.label}</div>
                <div style={{ fontSize: 12, opacity: isSel ? 0.85 : 0.65, marginTop: 2 }}>
                  🔊 {msg.text_nl}
                </div>
              </button>
            );
          })}
        </div>

        <div style={{ fontSize: 12, color: '#8a97a6', marginTop: 12 }}>
          Het bericht wordt als spraak afgespeeld in de headset (NL of EN, volgt de
          taal van de lopende experience).
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="refresh-btn" onClick={onCancel} disabled={sending}>
            Annuleren
          </button>
          <button
            className="refresh-btn"
            onClick={handleSubmit}
            disabled={!selected || sending}
            style={{
              background: '#2B6AC0',
              color: '#fff',
              fontWeight: 600,
              opacity: !selected || sending ? 0.6 : 1,
            }}
          >
            {sending ? 'Versturen…' : 'Verstuur'}
          </button>
        </div>
      </div>
    </div>
  );
}
